import React, { useEffect, useRef } from 'react';
import { animated, useSpring } from '@react-spring/web';
import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';

const Banner = props =>  {

    const { delay = 0, icon, message, onClose, onHide, onClick, onShow, orderIndex, title, visible } = props;

    const content = useRef({});
    const counter = useRef(0);
    const interaction = useRef(false);
    const interval = useRef(null);

    const [container, setContainer] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        top: -125
    }));

    const onAnimateComponents = async direction => {
        return new Promise(resolve => {
            setContainer({
                to: async next => {
                    await next({
                        opacity: direction === 'in' ? 1 : 0,
                        top: direction === 'in' ? 0 : -125
                    });
                    resolve();
                }
            });
        });
    }

    const onBannerClick = async () => {
        try {
            
            // update interaction flag
            interaction.current = true;

            // request banner to hide
            await onHideBanner();
            if(typeof(onClick) === 'function') {
                onClick();
                return;
            }

        } catch(e) {
            console.error(e.message);
        }
    }

    const onHideBanner = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                // animate components out of view
                await onAnimateComponents('out');
                content.current = {};

                // notifying subscribers of banner close
                if(typeof(onClose) === 'function') {
                    onClose();
                }

                // notifying root of banner close
                if(typeof(onHide) === 'function') {
                    onHide();
                }

                resolve();

            } catch(e) {
                reject(e);
            }
        });
    }

    const onResetBannerContent = async () => {
        try {

            // prevent moving forward if props have not changed
            if(content.current.message === props.message && content.current.title === props.title) {
                return;
            }

            // prevent moving forward if banner has just posted
            if(counter.current === 0) {
                return;
            }

            // reset counter and invalidate previous interval if found
            counter.current = 0;
            if(interval.current) {
                clearInterval(interval.current);
            }

            // hide components and allow them to re-enter with new content
            await onAnimateComponents('out');
            onRunAnimations();

        } catch(e) {
            console.error(e.message);
        }
    }

    const onRunAnimations = async () => {
        try {

            // prevent moving forward if props have not changed
            if(visible === false) {
                return;
            }

            // animate components into view
            content.current = props;
            await onAnimateComponents('in');

            // notify subscribers that banner has been displayed if applicable
            if(typeof(onShow) === 'function') {
                onShow();
            }

            // allow the banner to display for 5 seconds before attempting to auto close the banner
            setTimeout(async () => {
                if(interaction.current === false) {
                    await onHideBanner();
                }
            }, 5000);

        } catch(e) {
            console.error(e.message);
        }
    }

    const getContent = () => {
        if(Utils.isMobile() === true) {
            return (
                <div 
                className={'cursor-pointer'}
                onClick={onBannerClick}
                style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    boxShadow: '0px 10px 25px rgba(0,0,0,0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: 500,
                    overflow: 'hidden',
                    padding: 16,
                    textAlign: 'center'
                }}>
                    <img
                    src={icon || 'images/banner-icon.png'}
                    style={{
                        borderRadius: 15,
                        height: 50,
                        marginBottom: 12,
                        objectFit: 'cover',
                        width: 50
                    }} />
                    <span style={{
                        color: Appearance.colors.text(),
                        display: 'block',
                        fontFamily: 'Zen Dots',
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.5
                    }}>{title.toUpperCase()}</span>
                    <span style={{
                        color: Appearance.colors.text(),
                        display: 'block',
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.5
                    }}>{message}</span>
                </div>
            )
        }

        return (
            <div 
            className={'cursor-pointer'}
            onClick={onBannerClick}
            style={{
                ...Appearance.styles.unstyledPanel(),
                alignItems: 'center',
                boxShadow: '0px 10px 25px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'row',
                maxWidth: 600,
                overflow: 'hidden',
                padding: 18,
                pointerEvents: 'auto'
            }}>
                <img
                src={icon || 'images/banner-icon.png'}
                style={{
                    borderRadius: 15,
                    height: 40,
                    marginRight: 12,
                    objectFit: 'cover',
                    width: 40
                }} />
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1
                }}>
                    <span style={{ 
                        color: Appearance.colors.text(),
                        display: 'block',
                        fontFamily: 'Zen Dots',
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.5
                    }}>{title.toUpperCase()}</span>
                    <span style={{
                        color: Appearance.colors.text(),
                        display: 'block',
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.5
                    }}>{message}</span>
                </div>
            </div>
        )
    }

    useEffect(() => {
        onResetBannerContent();
    }, [message, title]);

    useEffect(() => {
        setTimeout(onRunAnimations, delay);
    }, [orderIndex]);

    return (
        <animated.div style={{
            marginBottom: 8,
            position: 'relative',
            ...container
        }}>
            {getContent()}
        </animated.div>
    )
}
export default Banner;
